import { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { getFileName, simplifyContentType } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface CacheInfo {
  entry: HAREntry;
  status: 'cached' | 'revalidated' | 'miss' | 'no-cache';
  cacheControl?: string;
  maxAge?: number;
  expires?: string;
  etag?: string;
  lastModified?: string;
  issues: string[];
}

function parseCacheControl(value: string): { maxAge?: number; noCache?: boolean; noStore?: boolean; mustRevalidate?: boolean } {
  const result: { maxAge?: number; noCache?: boolean; noStore?: boolean; mustRevalidate?: boolean } = {};

  const maxAgeMatch = value.match(/max-age=(\d+)/);
  if (maxAgeMatch) result.maxAge = parseInt(maxAgeMatch[1], 10);

  if (value.includes('no-cache')) result.noCache = true;
  if (value.includes('no-store')) result.noStore = true;
  if (value.includes('must-revalidate')) result.mustRevalidate = true;

  return result;
}

function getHeader(headers: Array<{ name: string; value: string }>, name: string): string | undefined {
  return headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value;
}

export function CacheAnalysis() {
  const { filteredEntries, selectEntry } = useHARStore();

  const { cacheInfo, stats } = useMemo(() => {
    const info: CacheInfo[] = [];
    let cached = 0;
    let revalidated = 0;
    let miss = 0;
    let noCache = 0;
    let couldBeCached = 0;

    filteredEntries.forEach(entry => {
      const { response } = entry;
      const issues: string[] = [];

      // Get cache headers
      const cacheControl = getHeader(response.headers, 'cache-control');
      const expires = getHeader(response.headers, 'expires');
      const etag = getHeader(response.headers, 'etag');
      const lastModified = getHeader(response.headers, 'last-modified');
      const age = getHeader(response.headers, 'age');

      const parsed = cacheControl ? parseCacheControl(cacheControl) : {};

      // Determine cache status
      let status: CacheInfo['status'] = 'miss';

      if (response.status === 304) {
        status = 'revalidated';
        revalidated++;
      } else if (parsed.noStore || parsed.noCache) {
        status = 'no-cache';
        noCache++;
      } else if (age && parseInt(age, 10) > 0) {
        status = 'cached';
        cached++;
      } else if (parsed.maxAge && parsed.maxAge > 0) {
        // Has cache headers but wasn't served from cache
        status = 'miss';
        miss++;
      } else {
        status = 'miss';
        miss++;
      }

      // Check for caching opportunities
      const contentType = simplifyContentType(response.content.mimeType);
      const isCacheable = ['js', 'css', 'image', 'font'].includes(contentType);
      const hasNoCacheHeaders = !cacheControl && !expires && !etag;

      if (isCacheable && hasNoCacheHeaders && response.status === 200) {
        issues.push('Static asset without cache headers');
        couldBeCached++;
      }

      if (parsed.maxAge !== undefined && parsed.maxAge < 86400 && isCacheable) {
        issues.push(`Short max-age (${parsed.maxAge}s) for static asset`);
      }

      if (etag && lastModified) {
        // Both validators present, OK
      } else if (!etag && !lastModified && parsed.maxAge) {
        issues.push('Missing ETag/Last-Modified for revalidation');
      }

      info.push({
        entry,
        status,
        cacheControl,
        maxAge: parsed.maxAge,
        expires,
        etag,
        lastModified,
        issues,
      });
    });

    return {
      cacheInfo: info,
      stats: { cached, revalidated, miss, noCache, couldBeCached },
    };
  }, [filteredEntries]);

  const issueCount = cacheInfo.filter(c => c.issues.length > 0).length;

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="text-center p-2 bg-green-50 dark:bg-green-500/10 rounded border border-green-100 dark:border-green-500/20">
            <div className="text-lg font-bold text-green-600 dark:text-green-400">{stats.cached}</div>
            <div className="text-[10px] uppercase font-bold text-slate-500">Cached</div>
          </div>
          <div className="text-center p-2 bg-blue-50 dark:bg-blue-500/10 rounded border border-blue-100 dark:border-blue-500/20">
            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{stats.revalidated}</div>
            <div className="text-[10px] uppercase font-bold text-slate-500 truncate" title="Revalidated">Reval.</div>
          </div>
          <div className="text-center p-2 bg-yellow-50 dark:bg-yellow-500/10 rounded border border-yellow-100 dark:border-yellow-500/20">
            <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{stats.miss}</div>
            <div className="text-[10px] uppercase font-bold text-slate-500">Miss</div>
          </div>
          <div className="text-center p-2 bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
            <div className="text-lg font-bold text-slate-600 dark:text-slate-400">{stats.noCache}</div>
            <div className="text-[10px] uppercase font-bold text-slate-500">None</div>
          </div>
        </div>

        {/* Opportunities */}
        {stats.couldBeCached > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-yellow-700 dark:text-yellow-400">
              <AlertCircle className="w-4 h-4" />
              <span className="font-bold">{stats.couldBeCached} static assets without cache headers</span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 font-medium">
              Adding cache headers could improve repeat visit performance.
            </p>
          </div>
        )}

        {/* Entries with issues */}
        {issueCount > 0 && (
          <div className="space-y-2">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Issues ({issueCount})</h3>
            <div className="space-y-1">
              {cacheInfo
                .filter(c => c.issues.length > 0)
                .slice(0, 20) // Show more since we have scroll
                .map((info, i) => (
                  <button
                    key={info.entry._id || i}
                    onClick={() => selectEntry(info.entry)}
                    className="w-full text-left p-2 rounded bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border border-black/5 dark:border-white/5"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-700 dark:text-slate-300 font-medium truncate">
                        {getFileName(info.entry.request.url)}
                      </span>
                      <span className={cn(
                        'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded',
                        info.status === 'cached' && 'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400',
                        info.status === 'revalidated' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400',
                        info.status === 'miss' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
                        info.status === 'no-cache' && 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                      )}>
                        {info.status === 'revalidated' ? 'reval.' : info.status}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 font-medium">
                      {info.issues.join(' · ')}
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
